import React from 'react';
import { colors } from '../../primitives/colors';
import Link from '../../primitives/link';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import Image from '../../primitives/image';
import Typography from '../../primitives/typography';
import Accordion, { AccordionButton, AccordionIcon, AccordionPanel } from '../../primitives/accordion';
import { breakpoints } from '../../primitives/tokens';
import { FaqQuestion, FaqsData } from './types/faqs-types';

interface AccordionSetProps {
    faqs: FaqsData,
    onDark?: boolean,
    onMediaContent?: boolean,
    columns?: number,
}

const AccordionSet = ({ faqs, onDark, onMediaContent, columns = 1 }: AccordionSetProps): JSX.Element => {
    const { headline, heading, byline, questions: faqsData } = faqs;

    // Extract questions from the nested structure (static computation)
    let questions: FaqQuestion[] = [];

    if (faqsData) {
        if (Array.isArray(faqsData)) {
            // Handle array structure
            questions = faqsData.length > 0 && Array.isArray(faqsData[0]?.questions)
                ? faqsData[0].questions || []
                : [];
        } else if (faqsData.questions && Array.isArray(faqsData.questions)) {
            // Handle direct questions structure
            questions = faqsData.questions || [];
        }
    }

    const casedHeading = apCaseOnlyTitleTags(headline) ?? apCaseOnlyTitleTags(heading) ?? null;

    const getItemKey = (question: string | undefined, index: number): string => {
        if (question) {
            // Create a short, stable ID from the question
            return question.replace(/[^a-zA-Z0-9]/g, '').toLowerCase().slice(0, 30);
        }
        return `faq-item-${index}`;
    };

    return (
        <div css={{ width: '100%' }}>
            {casedHeading !== '' && (
                <Typography
                    as="h3"
                    fontSize={{
                        dt: '2xl',
                        tb: 'xl',
                    }}
                    css={{
                        color: onDark ? colors.grey100 : colors.shadesBlack,
                        marginBottom: onMediaContent ? '12px' : '0',
                        textAlign: onMediaContent ? 'left' : 'center',
                    }}
                >
                    {casedHeading}
                </Typography>
            )}
            {byline && (
                <p
                    css={[RichTextEditorStyles, {
                        marginBottom: '40px',
                        fontSize: '20px',
                        lineHeight: '28px',
                        color: onDark ? colors.shadesWhite : colors.shadesBlack,
                    }]}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: byline || '' }}
                />
            )}
            <div css={{
                display: columns > 1 ? 'grid' : 'block',
                rowGap: columns > 1 ? '16px' : '0',
                columnGap: columns > 1 ? '64px' : '0',
                gridTemplateColumns: columns > 1 ? `repeat(${columns}, minmax(0, 1fr))` : 'unset',
                [breakpoints.tb]: {
                    display: 'block',
                    rowGap: '0',
                    columnGap: '0',
                },
            }}
            >
                {questions.map((pair, index) => {
                    const { question, answer, icon, url } = pair;
                    const isLast = index === questions.length - 1 && columns < 2;

                    return (
                        <div
                            css={{
                                position: 'relative',
                                borderBottomWidth: (!onDark || !isLast) ? '1px' : '0px',
                                borderColor: (!onDark || !isLast) ? colors.grey200 : 'transparent',
                                display: 'flex',
                                flexDirection: 'column',
                                alignSelf: 'flex-end',
                            }}
                            key={getItemKey(question, index)}
                        >
                            <Accordion
                                css={{
                                    margin: '0',
                                }}
                            >
                                <AccordionButton css={{
                                    padding: '16px 0',
                                    width: '100%',
                                }}
                                >
                                    <Typography
                                        fontSize="lg"
                                        fontWeight="medium"
                                        css={{
                                            color: colors.primaryOrange,
                                            transition: 'color 0.3s',
                                            width: '100%',
                                            '&:hover': {
                                                color: colors.primaryOrangeHover,
                                            },
                                            '[open] &': {
                                                color: colors.primaryOrange,
                                            },
                                        }}
                                    >
                                        {question}
                                    </Typography>
                                    <AccordionIcon />
                                </AccordionButton>
                                <AccordionPanel>
                                    <div css={{
                                        display: 'flex',
                                        color: onDark ? colors.grey100 : colors.shadesBlack,
                                        paddingBottom: '16px',
                                        flexDirection: 'column',
                                        [breakpoints.mb]: {
                                            flexDirection: 'row',
                                        },
                                    }}
                                    >
                                        {icon?.url && (
                                            <div css={{
                                                marginRight: '40px',
                                                width: '200px',
                                                height: '200px',
                                                flexShrink: 0,
                                                position: 'relative',
                                                '::before': {
                                                    display: 'block',
                                                    paddingTop: '100%',
                                                    content: "''",
                                                },
                                                [breakpoints.mb]: {
                                                    width: '100%',
                                                    maxWidth: '300px',
                                                    height: 'auto',
                                                    minHeight: '200px',
                                                    position: 'relative',
                                                    marginBottom: '20px',
                                                    marginRight: '0',
                                                    '::before': {
                                                        display: 'none',
                                                    },
                                                },
                                            }}
                                            >
                                                <div css={{
                                                    position: 'absolute',
                                                    top: '0',
                                                    right: '0',
                                                    bottom: '0',
                                                    left: '0',
                                                    width: '100%',
                                                    height: '100%',
                                                }}
                                                >
                                                    <Image
                                                        css={{
                                                            objectFit: 'cover',
                                                            position: 'absolute',
                                                            top: '0',
                                                            right: '0',
                                                            bottom: '0',
                                                            left: '0',
                                                            width: '100%',
                                                            height: '100%',
                                                            maxWidth: '100%',
                                                            maxHeight: '100%',
                                                        }}
                                                        image={icon}
                                                        alt={icon.title || ''}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div>
                                            <Typography
                                                fontSize={{
                                                    dt: 'lg',
                                                    tb: 'base',
                                                }}
                                                css={[RichTextEditorStyles]}
                                                dangerouslySetInnerHTML={{ __html: answer || '' }}
                                            />
                                            {!!url && (
                                                <div css={{
                                                    paddingBottom: '8px',
                                                    marginTop: '16px',
                                                }}
                                                >
                                                    <Link
                                                        css={{
                                                            marginTop: '40px',
                                                            fontWeight: 600,
                                                        }}
                                                        to={url}
                                                    >
                                                        Read More ›
                                                    </Link>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </AccordionPanel>
                            </Accordion>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default AccordionSet;
